import React, { useEffect, useState } from 'react';
import apiClient from '../api/client';
import { useAuth } from '../context/AuthContext';
import { applyTTSPrefs, langToSpeechLang } from '../utils/ttsUtils';
import { SpeakerIcon, StopIcon } from './icons';

// "Perla del giorno" — one thought per day at the top of Today. Shown once a
// day, dismissible, never blocking. Labels are inline (same pattern as the
// other components' local dictionaries) so i18n.js stays about the app UI.
const L = {
  it: { title: 'Perla del giorno', listen: 'Ascolta', close: 'Chiudi' },
  en: { title: 'Thought of the day', listen: 'Listen', close: 'Close' },
  es: { title: 'Pensamiento del día', listen: 'Escuchar', close: 'Cerrar' },
  de: { title: 'Gedanke des Tages', listen: 'Anhören', close: 'Schließen' },
  fr: { title: 'Pensée du jour', listen: 'Écouter', close: 'Fermer' },
  nl: { title: 'Gedachte van de dag', listen: 'Beluisteren', close: 'Sluiten' },
  pt: { title: 'Pensamento do dia', listen: 'Ouvir', close: 'Fechar' },
  ru: { title: 'Мысль дня', listen: 'Слушать', close: 'Закрыть' },
  pl: { title: 'Myśl dnia', listen: 'Posłuchaj', close: 'Zamknij' },
  ro: { title: 'Gândul zilei', listen: 'Ascultă', close: 'Închide' },
  zh: { title: '每日一言', listen: '朗读', close: '关闭' },
  ja: { title: '今日の一言', listen: '読み上げ', close: '閉じる' },
  hi: { title: 'आज का विचार', listen: 'सुनें', close: 'बंद करें' },
  ko: { title: '오늘의 한마디', listen: '듣기', close: '닫기' },
  ar: { title: 'حكمة اليوم', listen: 'استماع', close: 'إغلاق' },
};

// Typographic quotation marks per language — a quote set in the wrong marks
// looks foreign. CJK uses its own brackets, German/Polish/Romanian the low-high
// pair, French the spaced guillemets.
export const QUOTES = {
  it: ['«', '»'], es: ['«', '»'], pt: ['«', '»'], ru: ['«', '»'], ar: ['«', '»'],
  fr: ['« ', ' »'],
  de: ['„', '“'], pl: ['„', '”'], ro: ['„', '”'],
  en: ['“', '”'], nl: ['“', '”'], ko: ['“', '”'], hi: ['“', '”'],
  zh: ['「', '」'], ja: ['「', '」'],
};

const CACHE_KEY   = 'memo_pearl_cache';    // { date, text, author } — so the pearl shows up offline too
const DISMISS_KEY = 'memo_pearl_dismissed'; // date the user closed it
// What was actually shown, day by day. The pearl now depends on how full your
// day was, so the server can no longer recompute the past exactly — this is the
// honest record of "the pearls you have seen". Read by the Archive.
export const HISTORY_KEY = 'memo_pearl_history';

function remember(p) {
  try {
    const past = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
    const next = [p, ...past.filter(x => x.date !== p.date)].slice(0, 90);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
  } catch (_) {}
}

// The pearls shown on the most recent days BEFORE today, newest first. The
// server walks past all of them, so a phrase does not come round again for a
// couple of weeks.
//
// This used to send yesterday's alone, and that was not enough: there are 79
// pearls split across seven tones, the smallest holding six, so a quiet week
// kept landing on the same handful. Avoiding one day made it alternate between
// two — the 11th, 13th and 15th of August all showed the same sentence.
function recentIds(today, howMany = 14) {
  try {
    const past = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
    return past
      .filter(p => p?.date && p.date < today && p.id)
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, howMany)
      .map(p => p.id);
  } catch (_) { return []; }
}

function todayLocal() {
  try { return new Date().toLocaleDateString('en-CA'); } catch (_) { return new Date().toISOString().slice(0, 10); }
}
function readCache() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || 'null'); } catch (_) { return null; }
}

/**
 * @param {number|null} todayCount how many events the user has today, once the
 *        day has loaded. The pearl waits for it so a packed day can get a calm
 *        phrase instead of the tone of the weekday — but never waits forever.
 */
export default function PearlCard({ todayCount = null }) {
  const { user } = useAuth();
  const base = (user?.language || 'it').split('-')[0];
  const t = L[base] || L.en;

  const today = todayLocal();
  const [pearl, setPearl] = useState(() => {
    const c = readCache();
    // Per-day AND per-language: after a language change (which reloads the
    // app) the cached pearl is in the OLD language — refetch instead of
    // showing a half-translated card for the rest of the day.
    return c && c.date === today && c.lang === (user?.language || null) ? c : null;
  });
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === today; } catch (_) { return false; }
  });
  const [speaking, setSpeaking] = useState(false);

  // Fetch today's pearl once a day. A cached pearl for today is enough — this
  // keeps it to at most one request per device per day, and it still shows when
  // offline or on a connection too slow to answer.
  // Don't hold the card hostage to the day's events: if they're slow or the
  // request failed, ask for the pearl anyway after a few seconds.
  const [waitedForDay, setWaitedForDay] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setWaitedForDay(true), 6000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    // Pearl switched off → no daily request, no history writes (the render
    // guard below already hides the card).
    if (hidden || user?.pearl_enabled === false) return;
    if (pearl && pearl.date === today) return;
    if (todayCount === null && !waitedForDay) return; // still counting the day
    let cancelled = false;
    const params = { tz: Intl.DateTimeFormat().resolvedOptions().timeZone };
    if (todayCount !== null) params.load = todayCount; // the number only, never the titles
    const avoid = recentIds(today);
    if (avoid.length) params.avoid = avoid.join(',');
    apiClient.get('/pearl', { params })
      .then((res) => {
        if (cancelled || !res.data?.text) return;
        const fresh = {
          date: res.data.date || today, id: res.data.id,
          text: res.data.text, author: res.data.author,
          lang: user?.language || null, // cache is valid for THIS language only
        };
        setPearl(fresh);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(fresh)); } catch (_) {}
        remember(fresh);
      })
      .catch(() => { /* no pearl today — the card simply doesn't appear */ });
    return () => { cancelled = true; };
  }, [hidden, today, todayCount, waitedForDay, pearl, user?.pearl_enabled, user?.language]);

  // A pearl restored from cache was shown too — make sure it's in the history.
  useEffect(() => { if (pearl?.text && pearl.date === today) remember(pearl); }, [pearl, today]);

  // Stop any reading when leaving the page
  useEffect(() => () => { try { window.speechSynthesis?.cancel(); } catch (_) {} }, []);

  function dismiss() {
    setHidden(true);
    try { localStorage.setItem(DISMISS_KEY, today); } catch (_) {}
    try { window.speechSynthesis?.cancel(); } catch (_) {}
  }

  function speak() {
    if (!window.speechSynthesis || !pearl) return;
    if (speaking) { window.speechSynthesis.cancel(); setSpeaking(false); return; }
    const utt = new SpeechSynthesisUtterance(`${pearl.text} — ${pearl.author}`);
    utt.lang = langToSpeechLang(user?.language);
    applyTTSPrefs(utt);
    utt.onend = () => setSpeaking(false);
    utt.onerror = () => setSpeaking(false);
    setSpeaking(true);
    window.speechSynthesis.speak(utt);
  }

  if (hidden || !pearl?.text || user?.pearl_enabled === false) return null;

  // "Albert Einstein" → "A. Einstein", which is what Silvia asked for.
  //
  // Only where it makes sense. A single name (Confucio, Anonimo) is left whole
  // — "C." is not an attribution, it is a riddle. And a name written in a
  // script that has no initials, which is most of the Chinese, Japanese and
  // Arabic authors, is never touched: 孔子 is two characters and neither of
  // them is a "first name".
  const shortAuthor = (() => {
    const full = String(pearl.author || '').trim();
    if (!full) return '';
    if (!/[A-Za-zÀ-ÿ]/.test(full)) return full;          // nothing to abbreviate
    const parts = full.split(/\s+/);
    if (parts.length < 2) return full;                   // one name only
    const first = parts[0];
    if (first.length < 2 || first.endsWith('.')) return full;  // already short
    return first[0].toUpperCase() + '. ' + parts.slice(1).join(' ');
  })();

  return (
    /* Silvia's design, and every line of it is her instruction:
       no frame — the 6px accent border made a small thing look loud;
       the field itself carries the app's colour instead, as a wash, so it is
       still "the colour you set the app to" but a quote can be read on it;
       smaller, centred both ways, no quotation marks, the words in italic;
       and underneath, quieter, the name of the thing and who said it. */
    <div style={{
      position: 'relative',
      background: 'color-mix(in srgb, var(--color-primary) 22%, var(--color-card))',
      border: 'none',
      borderRadius: 'var(--radius, 14px)',
      padding: '14px 34px 12px',
      margin: '0 0 10px',
      minHeight: 92,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      textAlign: 'center',
    }}>
      {/* The two controls sit on top of the text rather than in a row above it,
          so that removing the header did not also remove the way out. */}
      <div style={{ position: 'absolute', top: 4, insetInlineEnd: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        {typeof window !== 'undefined' && window.speechSynthesis && (
          <button type="button" onClick={speak} title={t.listen} aria-label={t.listen}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 4, fontSize: '0.9rem',
              color: speaking ? '#c4614a' : 'var(--pearl-ink-soft)', display: 'flex', alignItems: 'center',
            }}>
            {speaking ? <StopIcon /> : <SpeakerIcon />}
          </button>
        )}
        <button type="button" onClick={dismiss} title={t.close} aria-label={t.close}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px',
            color: 'var(--pearl-ink-soft)', fontSize: '0.9rem', lineHeight: 1,
          }}>✕</button>
      </div>

      {/* No fixed height: German runs ~35% longer than Italian, Chinese ~40%
          shorter, and CJK wraps without spaces — the card grows with the text. */}
      <div style={{
        fontSize: '0.92rem', lineHeight: 1.5, color: 'var(--pearl-ink)',
        fontStyle: 'italic', overflowWrap: 'anywhere',
      }}>
        {pearl.text}
      </div>
      <div style={{
        fontSize: '0.72rem', fontWeight: 600, color: 'var(--pearl-ink-soft)',
        marginTop: 7, letterSpacing: '0.01em',
      }}>
        {t.title}{shortAuthor ? ' - ' + shortAuthor : ''}
      </div>
    </div>
  );
}
